import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ShortcutBadgeComponent } from '../shortcut-badge/shortcut-badge.component';
import { Hotkey } from '../../model/hotkey.model';
import type { IShortcutComboBlockChecker } from '../../types-interfaces/hotkey.options';

@Component({
  selector: 'app-shortcut-input',
  templateUrl: './shortcut-input.component.html',
  styleUrls: ['./shortcut-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslatePipe, ShortcutBadgeComponent],
})
export class ShortcutInputComponent implements AfterViewInit, OnChanges {
  @Input() combos: string[] = [];
  @Input() excludeButtonId?: string;
  @Input() excludeActionKey?: string;
  @Input() shortcutRegistry?: IShortcutComboBlockChecker;
  @Input() compact = false;
  @Input() autofocus = false;
  @Input() inputId?: string;
  /** When provided, combos for which this returns false are not added (e.g. reject command on Windows). */
  @Input() shouldIncludeCombo?: (combo: string) => boolean;

  @Output() combosChange = new EventEmitter<string[]>();
  @Output() hasErrorChange = new EventEmitter<boolean>();
  @Output() hasContentChange = new EventEmitter<boolean>();

  @ViewChild('shortcutInput') shortcutInputRef!: ElementRef<HTMLDivElement>;

  combosInput = signal<string[]>([]);
  comboPreview = signal<string>('');
  isFocused = signal(false);
  conflictError = signal<string | null>(null);
  validationError = signal<string | null>(null);
  reservedHint = signal<string | null>(null);
  canOverwriteConflict = signal<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['combos']) {
      const newCombos = this.combos ?? [];
      const prev = this.combosInput();
      const contentChanged =
        prev.length !== newCombos.length ||
        prev.some((c, i) => c !== newCombos[i]);
      if (contentChanged) {
        this.combosInput.set(newCombos.length ? [...newCombos] : []);
        this.comboPreview.set('');
        this.clearErrors();
        this.emitHasContent();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.autofocus) {
      setTimeout(() => this.shortcutInputRef?.nativeElement?.focus(), 0);
    }
  }

  onFocus(): void {
    this.isFocused.set(true);
  }

  onBlur(): void {
    this.isFocused.set(false);
  }

  onKeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.key === 'Enter') {
      this.addCurrentCombo();
      return;
    }

    const hasModifiers = event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;
    const key = event.key?.toLowerCase?.() ?? '';

    if (!hasModifiers && key === 'backspace') {
      const prev = this.comboPreview().trim();
      if (this.isSequence(prev)) {
        const parts = prev.split(/\s+/);
        parts.pop();
        const next = parts.join(' ');
        this.comboPreview.set(next);
        this.clearErrors();
        this.emitHasContent();
      } else {
        this.clearPreview();
      }
      return;
    }

    if (!hasModifiers && key && key.length === 1 && /^[a-z0-9]$/.test(key)) {
      const prev = this.comboPreview();
      const next = prev ? `${prev} ${key}` : key;
      this.comboPreview.set(next);
      this.validationError.set(null);
      this.reservedHint.set(null);
      this.conflictError.set(null);
      this.emitHasContent();
      this.emitHasError();
      return;
    }

    const parts: string[] = [];
    if (event.ctrlKey) parts.push('ctrl');
    if (event.metaKey) parts.push('command');
    if (event.altKey) parts.push('alt');
    if (event.shiftKey) parts.push('shift');
    if (key && !['control', 'meta', 'alt', 'shift'].includes(key)) {
      parts.push(Hotkey.toMousetrapKey(key));
    }
    const combo = parts.join('+');
    if (combo) {
      const normalized = combo.toLowerCase();
      this.comboPreview.set(normalized);
      this.validationError.set(null);
      this.emitHasContent();

      const partsSplit = normalized.split('+');
      if (partsSplit.length === 1 && partsSplit[0].length === 1) {
        this.validationError.set(_('hotkeys.shortcut-single-key'));
        this.emitHasError();
        return;
      }

      const reason = this.shortcutRegistry?.getComboBlockReason(
        normalized,
        this.excludeButtonId,
        this.excludeActionKey
      );
      if (reason === 'other_button' || reason === 'reserved') {
        this.conflictError.set(_('hotkeys.shortcut-conflict'));
        this.canOverwriteConflict.set(true);
        this.emitHasError();
        return;
      }

      this.reservedHint.set(null);
      this.conflictError.set(null);
      this.canOverwriteConflict.set(false);
      this.emitHasError();
      this.emitHasContent();
    }
  }

  private isSequence(value: string): boolean {
    return !value.includes('+');
  }

  addCurrentCombo(): void {
    const preview = this.comboPreview().trim().toLowerCase();
    if (!preview) return;

    if (this.shouldIncludeCombo && !this.shouldIncludeCombo(preview)) return;

    const isSeq = this.isSequence(preview);
    if (!isSeq) {
      const partsSplit = preview.split('+');
      if (partsSplit.length === 1 && partsSplit[0].length === 1) {
        this.validationError.set(_('hotkeys.shortcut-single-key'));
        this.emitHasError();
        return;
      }
    } else {
      const seqParts = preview.split(/\s+/).filter(Boolean);
      if (seqParts.length < 1) {
        return;
      }
    }

    const reason = this.shortcutRegistry?.getComboBlockReason(
      preview,
      this.excludeButtonId,
      this.excludeActionKey
    );
    if (reason === 'other_button' || reason === 'reserved') {
      this.conflictError.set(_('hotkeys.shortcut-conflict'));
      this.canOverwriteConflict.set(true);
      this.emitHasError();
      return;
    }

    const existing = this.combosInput();
    if (existing.includes(preview)) return;

    this.clearErrors();
    const next = [...existing, preview];
    this.combosInput.set(next);
    this.comboPreview.set('');
    this.combosChange.emit(next);
    this.emitHasContent();
    this.shortcutInputRef?.nativeElement?.focus();
  }

  removeCombo(index: number): void {
    const combos = [...this.combosInput()];
    combos.splice(index, 1);
    this.combosInput.set(combos);
    this.clearErrors();
    this.combosChange.emit(combos);
    this.emitHasContent();
    this.shortcutInputRef?.nativeElement?.focus();
  }

  clearPreview(): void {
    this.comboPreview.set('');
    this.clearErrors();
    this.emitHasContent();
    this.emitHasError();
    this.shortcutInputRef?.nativeElement?.focus();
  }

  private clearErrors(): void {
    this.validationError.set(null);
    this.conflictError.set(null);
    this.reservedHint.set(null);
    this.canOverwriteConflict.set(false);
    this.emitHasError();
  }

  /** Adds the preview combo despite conflict (user confirmed overwrite). */
  addCurrentComboWithOverwrite(): void {
    const preview = this.comboPreview().trim().toLowerCase();
    if (!preview) return;

    if (this.shouldIncludeCombo && !this.shouldIncludeCombo(preview)) return;

    const isSeq = this.isSequence(preview);
    if (!isSeq) {
      const partsSplit = preview.split('+');
      if (partsSplit.length === 1 && partsSplit[0].length === 1) {
        this.validationError.set(_('hotkeys.shortcut-single-key'));
        this.emitHasError();
        return;
      }
    } else {
      const seqParts = preview.split(/\s+/).filter(Boolean);
      if (seqParts.length < 1) return;
    }

    const existing = this.combosInput();
    if (existing.includes(preview)) return;

    this.conflictError.set(null);
    this.canOverwriteConflict.set(false);
    const next = [...existing, preview];
    this.combosInput.set(next);
    this.comboPreview.set('');
    this.combosChange.emit(next);
    this.emitHasContent();
    this.emitHasError();
    this.shortcutInputRef?.nativeElement?.focus();
  }

  private emitHasError(): void {
    this.hasErrorChange.emit(this.hasError());
  }

  private emitHasContent(): void {
    this.hasContentChange.emit(this.combosInput().length > 0 || !!this.comboPreview());
  }

  hasError(): boolean {
    return !!(this.validationError() || this.conflictError() || this.reservedHint());
  }

  getCombosForSave(): string[] {
    const fromInput = this.combosInput()
      .map((c) => c.trim().toLowerCase())
      .filter(Boolean);
    const preview = this.comboPreview().trim().toLowerCase();
    if (preview && !fromInput.includes(preview)) {
      return [...fromInput, preview];
    }
    return fromInput;
  }

  /** Allow parent to set validation error (e.g. when save re-validates). */
  setValidationError(message: string | null): void {
    this.validationError.set(message);
    this.emitHasError();
  }

  setConflictError(message: string | null): void {
    this.conflictError.set(message);
    this.emitHasError();
  }
}
